import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { Users, Plus, Save, Trash2, Pencil, X, Bell, ChevronDown, ChevronUp } from 'lucide-react';
import { Button, Spinner, ConfirmationModal, Checkbox, Header } from '@crc-evans/ui-components';
import { message } from 'antd';
import {
  fetchTeamMembersForProject,
  deleteTeamMember,
  addTeamMember,
  getAllNonCognitoUsers,
  addNonCognitoUser,
} from '../../redux/pihEquipmentDetails/action';
import { fetchRoles } from '../../redux/roles/action';
import { getUsers, getAccess } from '../../services/teamMembers';
import { tabs } from '../../utils/constants';
import './DPRSettingsTeamsTab.css';

const getCognitoValue = (key, arr) => {
  if (!Array.isArray(arr)) return '';
  const res = arr.find((obj) => obj.Name === key);
  return res ? res.Value : '';
};

const isWelderRole = (roleName) =>
  String(roleName || '').trim().toLowerCase() === 'welder';

const DPRSettingsTeamsTab = ({
  selectedProject,
  projectTeamMembersList,
  isProjectTeamMembersLoading,
  isAddingMemberInProgress,
  isDeletingInProgress,
  roles,
  nonCognitoUsersList,
  isAddingNonCognitoUser,
  projectTeamMembersAccessList,
  userAccessList,
  onFetchTeamMembersForProject,
  onDeleteTeamMember,
  onAddTeamMember,
  onFetchRoles,
  onGetAllNonCognitoUsers,
  onAddNonCognitoUser,
}) => {
  const [hoveredRow, setHoveredRow] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userOptions, setUserOptions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [isUserSearchLoading, setIsUserSearchLoading] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [accessToDsr, setAccessToDsr] = useState(false);
  const [accessToTbt, setAccessToTbt] = useState(false);
  const [isGuestUser, setIsGuestUser] = useState(false);
  const [isNormalUser, setIsNormalUser] = useState(false);
  const [selectedTabs, setSelectedTabs] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [permissions, setPermissions] = useState({});
  const [isSelectedUserAdmin, setIsSelectedUserAdmin] = useState(false);
  const [welderId, setWelderId] = useState('');
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const userDropdownRef = useRef(null);
  const roleDropdownRef = useRef(null);

  const list = Array.isArray(projectTeamMembersList) ? projectTeamMembersList : [];

  // Write access: use userAccessList from Redux, or fallback to localStorage (e.g. when using DPR/ModernLayout where sidebar may not set Redux)
  const isWriteAccess = (() => {
    const groups =
      (userAccessList && userAccessList.length > 0)
        ? userAccessList
        : (() => {
            try {
              return JSON.parse(localStorage.getItem('UserGroup') || '[]');
            } catch (e) {
              return [];
            }
          })();
    if (Array.isArray(groups) && (groups.includes('admin') || groups.includes('super-admin')))
      return true;
    if (projectTeamMembersAccessList?.[0]?.isGuestUser) return false;
    if (
      projectTeamMembersAccessList?.[0]?.isNormalUser &&
      !projectTeamMembersAccessList?.[0]?.tabPermissions?.Team?.write
    )
      return false;
    return true;
  })();

  useEffect(() => {
    if (selectedProject?.id) {
      onFetchTeamMembersForProject(selectedProject);
    }
  }, [selectedProject?.id, onFetchTeamMembersForProject]);

  useEffect(() => {
    onFetchRoles();
    onGetAllNonCognitoUsers?.();
  }, [onFetchRoles, onGetAllNonCognitoUsers]);

  useEffect(() => {
    if (!isAddingMemberInProgress && isSaving) {
      setIsSaving(false);
      setIsAddModalOpen(false);
      setEditRecord(null);
      resetModalFields();
    }
  }, [isAddingMemberInProgress, isSaving]);

  const prevDeletingInProgress = useRef(false);
  useEffect(() => {
    const wasDeleting = prevDeletingInProgress.current;
    prevDeletingInProgress.current = isDeletingInProgress;
    if (wasDeleting && !isDeletingInProgress && memberToDelete) {
      setIsDeleteModalOpen(false);
      setMemberToDelete(null);
    }
  }, [isDeletingInProgress, memberToDelete]);

  // Load users when Add modal opens so dropdown has options
  useEffect(() => {
    if (isAddModalOpen && !editRecord) {
      setUserDropdownOpen(false);
      setUserOptions([]);
      setIsUserSearchLoading(true);
      getUsers('')
        .then((res) => {
          const users = res?.users || [];
          const mapped = users
            .map((u) => {
              const fName = getCognitoValue('custom:first_name', u?.Attributes);
              const lName = getCognitoValue('custom:last_name', u?.Attributes);
              const email = getCognitoValue('email', u?.Attributes);
              const displayName =
                fName && lName ? `${fName} ${lName}` : getCognitoValue('name', u?.Attributes) || email;
              if (!displayName && !email) return null;
              return {
                Username: u.Username,
                email,
                firstName: fName || '',
                lastName: lName || '',
                displayName: displayName.trim() || email,
                Attributes: u.Attributes,
                isNonCognitoUser: false,
              };
            })
            .filter(Boolean);
          setUserOptions(mapped);
        })
        .catch(() => setUserOptions([]))
        .finally(() => setIsUserSearchLoading(false));
    }
  }, [isAddModalOpen, editRecord]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target)) setUserDropdownOpen(false);
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(e.target)) setRoleDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchAccess = async () => {
      if (selectedUser?.Username) {
        try {
          const response = await getAccess(selectedUser.Username);
          const groups = Array.isArray(response) ? response.map((x) => x.GroupName) : [];
          setIsSelectedUserAdmin(
            groups.includes('admin') || groups.includes('super-admin')
          );
          if (isSelectedUserAdmin) {
            setIsGuestUser(false);
            setIsNormalUser(false);
            setSelectedTabs([]);
            setPermissions({});
            setSelectAll(false);
          }
        } catch (e) {
          setIsSelectedUserAdmin(false);
        }
      } else {
        setIsSelectedUserAdmin(false);
      }
    };
    fetchAccess();
  }, [selectedUser?.Username]);

  const cognitoUsersList = (() => {
    let users = [];
    if (userOptions.length > 0) users = [...userOptions];
    if (Array.isArray(nonCognitoUsersList) && nonCognitoUsersList.length > 0) {
      const non = nonCognitoUsersList
        .map((u) => {
          const displayName = `${u?.firstName || ''} ${u?.lastName || ''}`.trim();
          if (!displayName) return null;
          return {
            ...u,
            displayName,
            Username: u.cognitoUserId,
            isNonCognitoUser: true,
            email: u.email || '',
            firstName: u.firstName || '',
            lastName: u.lastName || '',
          };
        })
        .filter(Boolean);
      users = [...users, ...non];
    }
    return users;
  })();

  function resetModalFields() {
    setSelectedUser(null);
    setSelectedRole(null);
    setUserSearch('');
    setUserOptions([]);
    setAccessToDsr(false);
    setAccessToTbt(false);
    setIsGuestUser(false);
    setIsNormalUser(false);
    setSelectedTabs([]);
    setSelectAll(false);
    setPermissions({});
    setWelderId('');
  }

  const handleAddClick = () => {
    setEditRecord(null);
    resetModalFields();
    setIsGuestUser(true);
    setSelectAll(false);
    setSelectedTabs([]);
    setPermissions({});
    setIsAddModalOpen(true);
  };

  const handleUserSearch = async (value) => {
    setUserSearch(value);
    if (!value || value.length < 2) {
      setUserOptions([]);
      return;
    }
    setIsUserSearchLoading(true);
    try {
      const res = await getUsers('', value);
      const users = res?.users || [];
      const mapped = users
        .map((u) => {
          const fName = getCognitoValue('custom:first_name', u?.Attributes);
          const lName = getCognitoValue('custom:last_name', u?.Attributes);
          const email = getCognitoValue('email', u?.Attributes);
          const displayName =
            fName && lName ? `${fName} ${lName}` : getCognitoValue('name', u?.Attributes) || email;
          if (!displayName && !email) return null;
          return {
            Username: u.Username,
            email,
            firstName: fName || '',
            lastName: lName || '',
            displayName: displayName.trim() || email,
            Attributes: u.Attributes,
            isNonCognitoUser: false,
          };
        })
        .filter(Boolean);
      setUserOptions(mapped);
    } catch (e) {
      setUserOptions([]);
    } finally {
      setIsUserSearchLoading(false);
    }
  };

  const handleEditClick = async (record) => {
    if (!isWriteAccess) return;
    setEditRecord(record);
    setAccessToDsr(!!record.accessToDsr);
    setAccessToTbt(!!record.accessToTbt);
    setIsGuestUser(!!record.isGuestUser);
    setIsNormalUser(!!record.isNormalUser);
    setSelectedTabs(Array.isArray(record.selectedTabs) ? record.selectedTabs : []);
    setPermissions(typeof record.tabPermissions === 'object' ? record.tabPermissions : {});
    setSelectAll(Array.isArray(record.selectedTabs) && record.selectedTabs.length === tabs.length);
    setWelderId(record.welderId || '');

    let userData = cognitoUsersList.find((c) => c.email === record.email);
    if (!userData && record.email) {
      try {
        const res = await getUsers('', record.email);
        const users = res?.users || [];
        if (users.length > 0) {
          const u = users[0];
          const fName = getCognitoValue('custom:first_name', u?.Attributes);
          const lName = getCognitoValue('custom:last_name', u?.Attributes);
          userData = {
            Username: u.Username,
            email: getCognitoValue('email', u?.Attributes),
            firstName: fName || '',
            lastName: lName || '',
            displayName: [fName, lName].filter(Boolean).join(' ') || record.email,
            Attributes: u.Attributes,
            isNonCognitoUser: false,
          };
        }
      } catch (e) {
        // use record as fallback
        userData = {
          Username: record.cognitoUserId,
          email: record.email,
          firstName: record.firstName || '',
          lastName: record.lastName || '',
          displayName: [record.firstName, record.lastName].filter(Boolean).join(' ') || record.email,
          isNonCognitoUser: !!record.isNonCognitoUser,
        };
      }
    }
    if (!userData && record.isNonCognitoUser) {
      userData = {
        Username: record.cognitoUserId,
        email: record.email || '',
        firstName: record.firstName || '',
        lastName: record.lastName || '',
        displayName: [record.firstName, record.lastName].filter(Boolean).join(' ') || '—',
        isNonCognitoUser: true,
      };
    }
    setSelectedUser(userData || null);
    setUserSearch(userData?.displayName || '');
    const roleData = (roles || []).find((r) => (r.name || r.roleName) === record.roleName);
    setSelectedRole(roleData || null);
    setIsAddModalOpen(true);
  };

  const handleNotificationClick = (record) => {
    if (!isWriteAccess) return;
    message.info('Send notification – use main project Team tab if needed.');
  };

  const buildPayload = () => {
    const roleId = selectedRole?.id ?? selectedRole?.roleId;
    const email = selectedUser?.Attributes
      ? getCognitoValue('email', selectedUser.Attributes)
      : selectedUser?.email || '';
    const firstName = selectedUser?.Attributes
      ? getCognitoValue('custom:first_name', selectedUser.Attributes) ||
        getCognitoValue('name', selectedUser.Attributes)
      : selectedUser?.firstName || '';
    const lastName = selectedUser?.Attributes
      ? getCognitoValue('custom:last_name', selectedUser.Attributes)
      : selectedUser?.lastName || '';

    return {
      cognitoUserId: selectedUser.Username,
      email,
      firstName,
      lastName,
      projectId: selectedProject.id,
      roleId: Number(roleId),
      accessToDsr,
      accessToTbt: accessToTbt ? 1 : 0,
      isGuestUser,
      isNormalUser,
      selectedTabs,
      tabPermissions: isNormalUser ? permissions : {},
      isNonCognitoUser: !!selectedUser?.isNonCognitoUser,
      ...(isWelderRole(selectedRole?.name ?? selectedRole?.roleName) ? { welderId: welderId || '' } : {}),
    };
  };

  const handleAddSubmit = () => {
    if (!selectedUser || !selectedRole || !selectedProject) {
      message.warning('Please select a user and a job title.');
      return;
    }
    const roleId = selectedRole.id ?? selectedRole.roleId;
    if (!roleId) {
      message.warning('Please select a job title.');
      return;
    }
    setIsSaving(true);
    onAddTeamMember({
      addMemberPayload: buildPayload(),
      selectedProject,
    });
  };

  const handleDeleteClick = (record) => {
    setMemberToDelete(record);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!memberToDelete || !selectedProject) return;
    onDeleteTeamMember({
      data: {
        teamMemberData: memberToDelete,
        selectedProject,
      },
    });
    // Modal closes when isDeletingInProgress becomes false (useEffect)
  };

  const handleGuestUserChange = (checked) => {
    setIsGuestUser(checked);
    if (checked) {
      setIsNormalUser(false);
      setSelectAll(false);
      setSelectedTabs([]);
      setPermissions({});
    }
  };

  const handleNormalUserChange = (checked) => {
    setIsNormalUser(checked);
    if (checked) {
      setIsGuestUser(false);
      setSelectAll(true);
      const allTabs = tabs.map((t) => t.label);
      setSelectedTabs(allTabs);
      const updatedPermissions = {};
      allTabs.forEach((tab) => {
        updatedPermissions[tab] = { read: true, write: false };
      });
      setPermissions(updatedPermissions);
    } else {
      handleGuestUserChange(true);
    }
  };

  const handleSelectAllChange = (checked) => {
    setSelectAll(checked);
    if (checked) {
      const allTabs = tabs.map((t) => t.label);
      setSelectedTabs(allTabs);
      const updatedPermissions = {};
      allTabs.forEach((tab) => {
        updatedPermissions[tab] = { read: true, write: false };
      });
      setPermissions(updatedPermissions);
    } else {
      setSelectedTabs([]);
      setPermissions({});
    }
  };

  const handleTabChange = (checkedValues) => {
    setSelectedTabs(checkedValues);
    setSelectAll(checkedValues.length === tabs.length);
    const updatedPermissions = { ...permissions };
    checkedValues.forEach((tabLabel) => {
      if (!updatedPermissions[tabLabel]) {
        updatedPermissions[tabLabel] = { read: true, write: false };
      }
    });
    Object.keys(updatedPermissions).forEach((tabLabel) => {
      if (!checkedValues.includes(tabLabel)) delete updatedPermissions[tabLabel];
    });
    setPermissions(updatedPermissions);
  };

  const handlePermissionChange = (tabLabel, permissionType, isChecked) => {
    setPermissions((prev) => ({
      ...prev,
      [tabLabel]: { ...(prev[tabLabel] || {}), [permissionType]: isChecked },
    }));
  };

  const handleCloseAddModal = () => {
    if (!isSaving) {
      setIsAddModalOpen(false);
      setEditRecord(null);
      resetModalFields();
    }
  };

  return (
    <div className="dpr-settings-teams-tab">
      {isProjectTeamMembersLoading && (
        <div className="dpr-settings-teams-tab-loading">
          <Spinner variant="inline" size="medium" />
        </div>
      )}

      <div className="dpr-settings-teams-tab-card">
        <div className="dpr-settings-teams-tab-header">
          <div className="dpr-settings-teams-tab-header-left">
            <div className="dpr-settings-teams-tab-icon-wrap">
              <Users size={24} />
            </div>
            <div>
              <div className="dpr-settings-teams-tab-title-row">
                <h3 className="dpr-settings-teams-tab-title">Teams</h3>
                <span className="dpr-settings-teams-tab-summary">
                  Total: {list.length} {list.length === 1 ? 'member' : 'members'}
                </span>
              </div>
              <p className="dpr-settings-teams-tab-description">
                Manage team members, roles, and contact information.
              </p>
            </div>
          </div>
          <Button
            label="Add Team Member"
            variant="primary"
            size="small"
            icon={Plus}
            onClick={handleAddClick}
            disabled={!isWriteAccess}
          />
        </div>

        <div className="dpr-settings-teams-tab-table-wrap">
          <div className="dpr-settings-teams-tab-thead">
            <div className="dpr-settings-teams-tab-row dpr-settings-teams-tab-header-row">
              <div className="dpr-settings-teams-tab-cell dpr-settings-teams-tab-cell-name dpr-settings-teams-tab-header-cell">NAME</div>
              <div className="dpr-settings-teams-tab-cell dpr-settings-teams-tab-cell-email dpr-settings-teams-tab-header-cell">EMAIL</div>
              <div className="dpr-settings-teams-tab-cell dpr-settings-teams-tab-cell-role dpr-settings-teams-tab-header-cell">JOB TITLE</div>
              <div className="dpr-settings-teams-tab-cell dpr-settings-teams-tab-cell-dsr dpr-settings-teams-tab-header-cell">DSR</div>
              <div className="dpr-settings-teams-tab-cell dpr-settings-teams-tab-cell-comm dpr-settings-teams-tab-header-cell">COMMUNICATIONS</div>
              <div className="dpr-settings-teams-tab-cell dpr-settings-teams-tab-cell-actions dpr-settings-teams-tab-header-cell">ACTIONS</div>
            </div>
          </div>

          <div className="dpr-settings-teams-tab-rows">
            {list.length === 0 ? (
              <div className="dpr-settings-teams-tab-empty">
                No team members yet. Click &quot;Add Team Member&quot; to add one.
              </div>
            ) : (
              list.map((row) => (
                <div
                  key={row.id}
                  className="dpr-settings-teams-tab-row"
                  onMouseEnter={() => setHoveredRow(row.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  style={{
                    borderColor: hoveredRow === row.id ? '#00C9A7' : undefined,
                  }}
                >
                  <div className="dpr-settings-teams-tab-cell dpr-settings-teams-tab-cell-name">
                    {row.firstName || row.lastName
                      ? [row.firstName, row.lastName].filter(Boolean).join(' ')
                      : row.email || '—'}
                  </div>
                  <div className="dpr-settings-teams-tab-cell dpr-settings-teams-tab-cell-email">
                    {!row.isNonCognitoUser ? row.email || '—' : '—'}
                  </div>
                  <div className="dpr-settings-teams-tab-cell dpr-settings-teams-tab-cell-role">
                    {row.roleName || '—'}
                  </div>
                  <div className="dpr-settings-teams-tab-cell dpr-settings-teams-tab-cell-dsr dpr-settings-teams-tab-cell-check">
                    <Checkbox checked={!!row.accessToDsr} disabled borderColor="#00C9A7" onChange={() => {}} />
                  </div>
                  <div className="dpr-settings-teams-tab-cell dpr-settings-teams-tab-cell-comm dpr-settings-teams-tab-cell-check">
                    <Checkbox checked={!!row.accessToTbt} disabled borderColor="#00C9A7" onChange={() => {}} />
                  </div>
                  <div className="dpr-settings-teams-tab-cell dpr-settings-teams-tab-cell-actions">
                    <button
                      type="button"
                      className="dpr-settings-teams-tab-action-btn"
                      onClick={() => handleEditClick(row)}
                      title="Update"
                      aria-label="Update"
                      disabled={!isWriteAccess}
                      style={{ opacity: isWriteAccess ? 1 : 0.7 }}
                    >
                      <Pencil size={18} />
                    </button>
                    <button
                      type="button"
                      className="dpr-settings-teams-tab-action-btn"
                      onClick={() => handleNotificationClick(row)}
                      title="Send Notification"
                      aria-label="Send Notification"
                      disabled={!isWriteAccess}
                      style={{ opacity: isWriteAccess ? 1 : 0.7 }}
                    >
                      <Bell size={18} />
                    </button>
                    <button
                      type="button"
                      className="dpr-settings-teams-tab-action-btn dpr-settings-teams-tab-action-delete"
                      onClick={() => handleDeleteClick(row)}
                      title="Delete Member"
                      aria-label="Delete"
                      disabled={!isWriteAccess}
                      style={{ opacity: isWriteAccess ? 1 : 0.7 }}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="dpr-settings-teams-tab-footer">
          <Button
            label="Save"
            variant="secondary"
            icon={Save}
            onClick={() => {
              if (selectedProject?.id) onFetchTeamMembersForProject(selectedProject);
            }}
            disabled={isProjectTeamMembersLoading}
          />
        </div>
      </div>

      {/* Add / Edit Team Member modal */}
      {isAddModalOpen && (
        <div className="dpr-settings-teams-tab-modal-backdrop" onClick={handleCloseAddModal}>
          <div className="dpr-settings-teams-tab-modal dpr-settings-teams-tab-modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="dpr-settings-teams-tab-modal-head">
              <h3 className="dpr-settings-teams-tab-modal-title">
                {editRecord ? 'Update Member To Project' : 'Add Member To Project'}
              </h3>
              <button
                type="button"
                className="dpr-settings-teams-tab-modal-close"
                onClick={handleCloseAddModal}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="dpr-settings-teams-tab-modal-body">
              <div className="dpr-settings-teams-tab-modal-field">
                <label>User</label>
                <div ref={userDropdownRef} className="dpr-settings-teams-tab-user-dropdown">
                  <div
                    className="dpr-settings-teams-tab-select-trigger"
                    onClick={() => !editRecord && setUserDropdownOpen(!userDropdownOpen)}
                    style={{ cursor: editRecord ? 'default' : 'pointer', opacity: editRecord ? 0.8 : 1 }}
                  >
                    <span className="dpr-settings-teams-tab-select-value">
                      {selectedUser
                        ? `${selectedUser.displayName}${selectedUser.email ? ` (${selectedUser.email})` : ''}`
                        : isUserSearchLoading
                        ? 'Loading...'
                        : 'Search by user name or email'}
                    </span>
                    {!editRecord && (
                      <span className="dpr-settings-teams-tab-select-chevron">
                        {userDropdownOpen ? <ChevronUp size={18} color="#6B7280" /> : <ChevronDown size={18} color="#6B7280" />}
                      </span>
                    )}
                  </div>
                  {userDropdownOpen && !editRecord && (
                    <div className="dpr-settings-teams-tab-user-list-wrap">
                      <input
                        type="text"
                        placeholder="Type to filter..."
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        className="dpr-settings-teams-tab-user-filter"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="dpr-settings-teams-tab-user-list">
                        {(userSearch.trim()
                          ? userOptions.filter(
                              (u) =>
                                (u.displayName || '').toLowerCase().includes(userSearch.toLowerCase()) ||
                                (u.email || '').toLowerCase().includes(userSearch.toLowerCase())
                            )
                          : userOptions
                        )
                          .slice(0, 50)
                          .map((u) => (
                            <div
                              key={u.Username}
                              className="dpr-settings-teams-tab-user-opt"
                              onClick={() => {
                                setSelectedUser(u);
                                setUserSearch('');
                                setUserDropdownOpen(false);
                              }}
                            >
                              {u.displayName} {u.email ? `(${u.email})` : ''}
                            </div>
                          ))}
                        {userOptions.length === 0 && !isUserSearchLoading && (
                          <div className="dpr-settings-teams-tab-user-opt" style={{ color: '#94A3B8', cursor: 'default' }}>
                            No users found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="dpr-settings-teams-tab-modal-field">
                <label>Job title</label>
                <div ref={roleDropdownRef} style={{ position: 'relative', width: '100%' }}>
                  <div
                    className="dpr-settings-teams-tab-select-trigger"
                    onClick={() => setRoleDropdownOpen(!roleDropdownOpen)}
                    style={{
                      border: roleDropdownOpen ? '2px solid #3B82F6' : '1px solid #E2E8F0',
                    }}
                  >
                    <span className="dpr-settings-teams-tab-select-value">
                      {selectedRole ? (selectedRole.name ?? selectedRole.roleName ?? '') : 'Select job title'}
                    </span>
                    <span className="dpr-settings-teams-tab-select-chevron">
                      {roleDropdownOpen ? <ChevronUp size={18} color="#6B7280" /> : <ChevronDown size={18} color="#6B7280" />}
                    </span>
                  </div>
                  {roleDropdownOpen && (
                    <div
                      className="dpr-settings-teams-tab-role-list"
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        zIndex: 9999,
                        backgroundColor: '#fff',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        padding: '6px 0',
                        maxHeight: '220px',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                      }}
                    >
                      {(roles || []).map((r) => (
                        <div
                          key={r.id ?? r.roleId}
                          className="dpr-settings-teams-tab-role-opt"
                          onClick={() => {
                            setSelectedRole(r);
                            setRoleDropdownOpen(false);
                            if (!isWelderRole(r?.name ?? r?.roleName)) setWelderId('');
                          }}
                        >
                          {r.name ?? r.roleName ?? ''}
                        </div>
                      ))}
                      {(roles || []).length === 0 && (
                        <div style={{ padding: '8px 12px', color: '#94A3B8', fontSize: '14px' }}>No roles loaded</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {isWelderRole(selectedRole?.name ?? selectedRole?.roleName) && (
                <div className="dpr-settings-teams-tab-modal-field">
                  <label>Welder ID</label>
                  <input
                    type="text"
                    value={welderId}
                    onChange={(e) => setWelderId(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #E2E8F0' }}
                    placeholder="Welder ID"
                  />
                </div>
              )}

              <div className="dpr-settings-teams-tab-modal-checkbox-section">
                <div className="dpr-settings-teams-tab-modal-checkbox-row">
                  <Checkbox
                    label="To be listed in DSR"
                    checked={accessToDsr}
                    onChange={(checked) => setAccessToDsr(!!checked)}
                    borderColor="#00C9A7"
                  />
                  <Checkbox
                    label="To be listed in Communications"
                    checked={!!accessToTbt}
                    onChange={(checked) => setAccessToTbt(checked ? 1 : 0)}
                    borderColor="#00C9A7"
                  />
                </div>

                <div className="dpr-settings-teams-tab-modal-checkbox-row">
                  <Checkbox
                    label="Guest User"
                    checked={isGuestUser}
                    onChange={(checked) => handleGuestUserChange(!!checked)}
                    disabled={isSelectedUserAdmin}
                    borderColor="#00C9A7"
                  />
                  <Checkbox
                    label="Normal User"
                    checked={isNormalUser}
                    onChange={(checked) => handleNormalUserChange(!!checked)}
                    disabled={isSelectedUserAdmin}
                    borderColor="#00C9A7"
                  />
                </div>

                {!isSelectedUserAdmin && (
                  <>
                    <div className="dpr-settings-teams-tab-modal-checkbox-row dpr-settings-teams-tab-select-all-row">
                      <Checkbox
                        label="Select ALL"
                        checked={selectAll}
                        onChange={(checked) => handleSelectAllChange(!!checked)}
                        borderColor="#00C9A7"
                      />
                    </div>
                    <div className="dpr-settings-teams-tab-tabs-grid">
                      {tabs.map((tab) => (
                        <div key={tab.label} className="dpr-settings-teams-tab-tab-row">
                          <Checkbox
                            label={tab.label}
                            checked={selectedTabs.includes(tab.label)}
                            onChange={(checked) => {
                              const next = checked
                                ? [...selectedTabs, tab.label]
                                : selectedTabs.filter((t) => t !== tab.label);
                              handleTabChange(next);
                            }}
                            borderColor="#00C9A7"
                          />
                          {isNormalUser && selectedTabs.includes(tab.label) && permissions[tab.label] && (
                            <div className="dpr-settings-teams-tab-permissions">
                              <Checkbox
                                label="Read"
                                checked={!!permissions[tab.label]?.read}
                                onChange={(checked) => handlePermissionChange(tab.label, 'read', !!checked)}
                                borderColor="#00C9A7"
                              />
                              <Checkbox
                                label="Write"
                                checked={!!permissions[tab.label]?.write}
                                onChange={(checked) => handlePermissionChange(tab.label, 'write', !!checked)}
                                borderColor="#00C9A7"
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {isSelectedUserAdmin && (
                <div className="dpr-settings-teams-tab-warn-box">
                  The selected user has an Admin role and will have full access to the project.
                </div>
              )}

              {!isSelectedUserAdmin && isGuestUser && selectedTabs.length > 0 && (
                <div className="dpr-settings-teams-tab-warn-box">
                  Guest users are granted view-only access.
                </div>
              )}
            </div>
            <div className="dpr-settings-teams-tab-modal-foot">
              <Button label="Cancel" variant="tertiary" onClick={handleCloseAddModal} size="medium" disabled={isSaving} />
              <Button
                label={
                  isSaving
                    ? editRecord
                      ? 'Updating…'
                      : 'Adding…'
                    : editRecord
                    ? 'Update'
                    : 'Add'
                }
                variant="primary"
                onClick={handleAddSubmit}
                disabled={isSaving || !selectedUser || !selectedRole}
                size="medium"
              />
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        title="Delete Team Member"
        message={`Are you sure you want to delete ${memberToDelete ? [memberToDelete.firstName, memberToDelete.lastName].filter(Boolean).join(' ') || memberToDelete.email : 'this member'}?`}
        confirmText={isDeletingInProgress ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        isOpen={isDeleteModalOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setMemberToDelete(null);
        }}
        size="medium"
        confirmButtonProps={{
          style: { backgroundColor: '#EF4444', borderColor: '#EF4444' },
          disabled: isDeletingInProgress,
        }}
      />
    </div>
  );
};

const mapStateToProps = ({ equip, rolesReduceer }) => ({
  projectTeamMembersList: get(equip, 'projectTeamMembersList', []),
  isProjectTeamMembersLoading: get(equip, 'isProjectTeamMembersLoading', false),
  isAddingMemberInProgress: get(equip, 'isAddingMemberInProgress', false),
  isDeletingInProgress: get(equip, 'isDeletingInProgress', false),
  roles: get(rolesReduceer, 'roles', []),
  nonCognitoUsersList: get(equip, 'nonCognitoUsersList', []),
  isAddingNonCognitoUser: get(equip, 'isAddingNonCognitoUser', false),
  projectTeamMembersAccessList: get(equip, 'projectTeamMembersAccessList', []),
  userAccessList: get(equip, 'userAccessList', []),
});

const mapDispatchToProps = {
  onFetchTeamMembersForProject: fetchTeamMembersForProject,
  onDeleteTeamMember: deleteTeamMember,
  onAddTeamMember: addTeamMember,
  onFetchRoles: fetchRoles,
  onGetAllNonCognitoUsers: getAllNonCognitoUsers,
  onAddNonCognitoUser: addNonCognitoUser,
};


export default connect(mapStateToProps, mapDispatchToProps)(DPRSettingsTeamsTab);
